import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Pressable } from '@/components/ui/Pressable'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, RADIUS, SPACING } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import type { MessageReplyPreview } from '@/lib/types/messaging.types'

type ReplyContextTone = 'sent' | 'received' | 'composer'

interface ReplyContextProps {
  reply: MessageReplyPreview | null
  authorLabel: string
  tone: ReplyContextTone
  onPress?: () => void
  testID?: string
}

function replySummary(reply: MessageReplyPreview | null, t: (key: string) => string): string {
  if (!reply || reply.isDeleted) return t('messages.reply_deleted')
  if (reply.messageType === 'image') return t('messages.preview_image')
  if (reply.messageType === 'location') return t('messages.preview_location')
  if (reply.messageType === 'system') return t('messages.system')
  return reply.content?.trim() || t('messages.reply_message')
}

/**
 * One reply treatment shared by the bubble and composer. This makes a reply
 * legible without reproducing a whole message, and keeps the parent text in
 * the same private conversation instead of opening a second data surface.
 */
export function ReplyContext({ reply, authorLabel, tone, onPress, testID }: ReplyContextProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const isSent = tone === 'sent'
  const isComposer = tone === 'composer'
  const primary = isSent ? '#FFFFFF' : colors.textPrimary
  const secondary = isSent ? 'rgba(255,255,255,0.78)' : colors.textSecondary
  const accent = isSent ? 'rgba(255,255,255,0.82)' : colors.textAccent

  const content = (
    <View
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: SPACING.sm,
        minWidth: 0,
        paddingVertical: isComposer ? SPACING.xs : 6,
        paddingHorizontal: isComposer ? SPACING.sm : SPACING.sm,
        borderRadius: RADIUS.md,
        backgroundColor: isSent
          ? 'rgba(12,8,24,0.16)'
          : isComposer
            ? colors.surfaceSubtle
            : isDark
              ? 'rgba(255,255,255,0.06)'
              : 'rgba(26,21,37,0.05)',
      }}
    >
      <View
        style={{
          width: 3,
          borderRadius: RADIUS.full,
          backgroundColor: accent,
        }}
      />
      <View style={{ flex: 1, minWidth: 0, gap: 1 }}>
        <Text
          numberOfLines={1}
          style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: primary }}
        >
          {t('messages.replying_to', { name: authorLabel })}
        </Text>
        <Text numberOfLines={1} style={{ fontFamily: FONTS.body, fontSize: 12, color: secondary }}>
          {replySummary(reply, t)}
        </Text>
      </View>
    </View>
  )

  if (!onPress) return content

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('messages.reply_open_original')}
      testID={testID}
    >
      {content}
    </Pressable>
  )
}
